import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { SectionHeader } from "../components/UI";
import { ArrowLeft, Bell, Mail, MonitorSmartphone, Sparkles } from "lucide-react";

/*
 * SettingsNotifications , per-user notification channel preferences.
 * Stored client-side in localStorage (until email transport is wired).
 * The switches are functional: they persist and affect the frontend "read"
 * notification behavior + UI density immediately.
 */

const LS_KEY = "prophr_notif_prefs_v1";

const CATEGORIES = [
  { key: "case_assigned",    label: "New case assigned to me",       desc: "When HR routes a case into your queue.",             defaults: { in_app: true, email: true } },
  { key: "case_update",      label: "Updates on my reported cases",  desc: "Non-internal replies from HR, status changes.",      defaults: { in_app: true, email: true } },
  { key: "findings_pending",  label: "Findings awaiting my approval",  desc: "Company Admins only.",                                defaults: { in_app: true, email: true } },
  { key: "policy_signature", label: "Policies needing my signature", desc: "New or updated policies published to my workspace.", defaults: { in_app: true, email: false } },
  { key: "workflow_step",    label: "Workflow step assigned to me",  desc: "A workflow you own advanced to a step you must complete.", defaults: { in_app: true, email: false } },
  { key: "ticket_reply",     label: "Support ticket replies",        desc: "When PropHR support replies to your ticket.",         defaults: { in_app: true, email: true } },
  { key: "digest",           label: "Weekly digest",                 desc: "Monday summary of open items and workspace health.", defaults: { in_app: false, email: true } },
];

function loadPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const out = {};
  for (const c of CATEGORIES) out[c.key] = { ...c.defaults };
  return out;
}

function savePrefs(p) {
  localStorage.setItem(LS_KEY, JSON.stringify(p));
}

export default function SettingsNotifications() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dirty) {
      savePrefs(prefs);
      const t = setTimeout(() => setDirty(false), 1000);
      return () => clearTimeout(t);
    }
  }, [prefs, dirty]);

  const update = (key, channel, value) => {
    setPrefs((p) => ({ ...p, [key]: { ...p[key], [channel]: value } }));
    setDirty(true);
  };

  const save = () => {
    savePrefs(prefs);
    toast.success("Notification preferences saved");
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-notifications">
      <Link to="/settings" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL SETTINGS
      </Link>
      <SectionHeader
        title="Notification preferences"
        subtitle="Choose what pings you and how. Your changes save automatically."
        right={<button onClick={save} className="btn-primary">Save</button>}
      />

      <div className="border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[1fr_100px_100px] items-center px-4 py-3 border-b border-slate-200 bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          <div>Notification</div>
          <div className="text-center inline-flex items-center justify-center gap-1">
            <MonitorSmartphone size={11} strokeWidth={2}/> In-app
          </div>
          <div className="text-center inline-flex items-center justify-center gap-1">
            <Mail size={11} strokeWidth={2}/> Email
          </div>
        </div>
        {CATEGORIES.map((c) => {
          const p = prefs[c.key] || {};
          return (
            <div key={c.key} className="grid grid-cols-[1fr_100px_100px] items-center px-4 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors">
              <div className="pr-4">
                <div className="text-sm font-medium text-slate-900">{c.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
              </div>
              <Toggle checked={!!p.in_app} onChange={(v) => update(c.key, "in_app", v)} testid={`toggle-${c.key}-in-app`}/>
              <Toggle checked={!!p.email} onChange={(v) => update(c.key, "email", v)} testid={`toggle-${c.key}-email`}/>
            </div>
          );
        })}
      </div>

      <div className="mt-6 border border-blue-200 bg-blue-50 p-4 flex gap-3">
        <Bell size={14} strokeWidth={2} className="text-blue-700 shrink-0 mt-0.5"/>
        <div className="text-xs text-blue-900 leading-relaxed">
          <div className="font-semibold mb-1">In-app notifications are live now.</div>
          Email delivery is being rolled out , when enabled, PropHR will send notifications to {" "}
          <span className="font-mono">{user?.email}</span>. Toggle email off now to opt out ahead of time.
        </div>
      </div>

      <div className="mt-4 border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={13} strokeWidth={2} className="text-violet-700"/>
          <div className="eyebrow">Do-not-disturb hours (coming soon)</div>
        </div>
        <div className="text-xs text-slate-500">Pause non-urgent notifications during your quiet hours. This will land in the next release.</div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={
        "w-11 h-6 flex items-center rounded-full border transition-colors mx-auto " +
        (checked ? "bg-slate-900 border-slate-900 justify-end" : "bg-white border-slate-300 justify-start")
      }
    >
      <span className={"w-5 h-5 rounded-full mx-0.5 shadow " + (checked ? "bg-white" : "bg-slate-300")}/>
    </button>
  );
}
